import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { reportsAPI, formatApiError } from '@/lib/api';
import { Eye, ArrowLeft, Copy, Download, Loader2, ChevronDown, AlertTriangle, Settings, LogOut, Mail, Lock, FileDown } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <header className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-md border-b border-zinc-200">
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
        <Link to="/dashboard" className="flex items-center gap-2">
          <Eye className="w-6 h-6 text-brand" strokeWidth={2} />
          <span className="font-heading font-black text-lg tracking-tight text-zinc-900">ContractEye</span>
        </Link>
        <div className="flex items-center gap-4">
          <span className="font-body text-sm text-zinc-500 hidden sm:block">{user?.email}</span>
          <Link to="/settings" className="p-2 hover:bg-zinc-100 transition-colors"><Settings className="w-4 h-4 text-zinc-600" /></Link>
          <button onClick={async () => { await logout(); navigate('/'); }} className="p-2 hover:bg-zinc-100 transition-colors"><LogOut className="w-4 h-4 text-zinc-600" /></button>
        </div>
      </div>
    </header>
  );
}

function RiskScoreDisplay({ score }) {
  let color, label;
  if (score >= 7) { color = 'text-rose-600'; label = 'High Risk'; }
  else if (score >= 4) { color = 'text-amber-600'; label = 'Moderate Risk'; }
  else { color = 'text-emerald-600'; label = 'Low Risk'; }
  return (
    <div className="text-center" data-testid="risk-score-display">
      <div className={`font-mono font-bold text-6xl ${color}`}>{score}</div>
      <div className="font-mono text-sm text-zinc-400 mt-1">/ 10</div>
      <div className={`font-body text-sm font-bold mt-2 ${color}`}>{label}</div>
    </div>
  );
}

function SeverityBadge({ severity }) {
  const s = severity?.toLowerCase();
  if (s === 'high') return <span className="bg-rose-100 text-rose-700 border border-rose-200 px-2 py-0.5 text-xs font-bold uppercase tracking-wider" data-testid="severity-high">High</span>;
  if (s === 'medium') return <span className="bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 text-xs font-bold uppercase tracking-wider" data-testid="severity-medium">Medium</span>;
  return <span className="bg-emerald-100 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-xs font-bold uppercase tracking-wider" data-testid="severity-low">Low</span>;
}

function CopyButton({ text, label }) {
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    toast.success(`${label || 'Content'} copied to clipboard`);
  };
  return (
    <button onClick={handleCopy} className="flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-600 transition-colors" data-testid={`copy-${label?.toLowerCase().replace(/\s/g, '-')}-btn`}>
      <Copy className="w-3 h-3" /> Copy
    </button>
  );
}

function CollapsibleSection({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-zinc-200 bg-white">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-6 py-4 hover:bg-zinc-50 transition-colors">
        <span className="font-heading font-bold text-sm text-zinc-900">{title}</span>
        <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-6 pb-6 border-t border-zinc-100">{children}</div>}
    </div>
  );
}

function downloadReport(report) {
  const lines = [];
  lines.push(`CONTRACT RISK ANALYSIS REPORT`);
  lines.push(`${'='.repeat(50)}`);
  lines.push(`File: ${report.file_name}`);
  lines.push(`Date: ${new Date(report.created_at).toLocaleString()}`);
  lines.push(`Risk Score: ${report.risk_score}/10`);
  lines.push(`\n--- EXECUTIVE SUMMARY ---\n${report.executive_summary}`);
  lines.push(`\n--- KEY OBLIGATIONS ---`);
  (report.key_obligations || []).forEach((o, i) => lines.push(`${i + 1}. ${o}`));
  lines.push(`\n--- RISK FLAGS ---`);
  (report.risk_flags || []).forEach(f => lines.push(`[${f.severity}] ${f.category}: ${f.concern}`));
  lines.push(`\n--- SUGGESTED REVISIONS ---`);
  (report.suggested_revisions || []).forEach((r, i) => lines.push(`${i + 1}. ${r}`));
  lines.push(`\n--- LAWYER REVIEW POINTS ---`);
  (report.lawyer_review_points || []).forEach((p, i) => lines.push(`${i + 1}. ${p}`));
  lines.push(`\n${'='.repeat(50)}`);
  lines.push(`DISCLAIMER: This report provides risk insights only and does not constitute legal advice.`);

  const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `risk-report-${report.file_name || 'contract'}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function AnalysisPage() {
  const { reportId } = useParams();
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailText, setEmailText] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);

  const isProPlus = user?.plan_type === 'pro_plus' || user?.role === 'admin';

  const handleGenerateEmail = async () => {
    if (!isProPlus) {
      toast.error('Negotiation emails are a Pro Plus feature. Upgrade in Settings.');
      return;
    }
    setEmailLoading(true);
    try {
      const API_BASE = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_BASE}/api/reports/${reportId}/negotiation-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ report_id: reportId, sender_name: user?.name || '', recipient_name: '', tone: 'professional' }),
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || 'Failed to generate email');
      }
      const data = await res.json();
      setEmailText(data.email);
      setShowEmailModal(true);
    } catch (err) {
      toast.error(err.message || 'Failed to generate negotiation email');
    } finally {
      setEmailLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await reportsAPI.get(reportId);
        setReport(res.data.report);
      } catch (err) {
        setError(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [reportId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-brand animate-spin" />
          <p className="font-body text-sm text-zinc-500">Loading report...</p>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen bg-zinc-50">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <div className="text-center">
            <p className="font-body text-sm text-rose-600 mb-4">{error || 'Report not found'}</p>
            <Link to="/dashboard" className="text-brand font-body text-sm underline">Back to Dashboard</Link>
          </div>
        </div>
      </div>
    );
  }

  const clauses = report.extracted_clauses || {};
  const clauseLabels = {
    payment_terms: 'Payment Terms', termination: 'Termination', liability: 'Liability',
    indemnity: 'Indemnity', confidentiality: 'Confidentiality', exclusivity: 'Exclusivity',
    renewal: 'Renewal', governing_law: 'Governing Law', dispute_resolution: 'Dispute Resolution',
    ip_ownership: 'IP Ownership', non_compete: 'Non-Compete',
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="analysis-page">
      <Navbar />
      <main className="pt-24 pb-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <Link to="/dashboard" className="flex items-center gap-1 font-body text-sm text-zinc-500 hover:text-zinc-700" data-testid="back-to-dashboard">
              <ArrowLeft className="w-4 h-4" /> Dashboard
            </Link>
          </div>

          {/* Disclaimer */}
          <div className="bg-amber-50 border border-amber-200 px-4 py-3 mb-6" data-testid="analysis-disclaimer">
            <p className="font-body text-xs text-amber-800">
              <strong>Disclaimer:</strong> This analysis provides risk insights only and does not constitute legal advice. Always consult a qualified attorney.
            </p>
          </div>

          <div className="grid lg:grid-cols-12 gap-6">
            {/* Main content - left 8 cols */}
            <div className="lg:col-span-8 space-y-4">
              {/* Header */}
              <div className="bg-white border border-zinc-200 p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 font-body mb-1">Analysis Report</p>
                    <h1 className="font-heading font-bold text-xl tracking-tight text-zinc-900">{report.file_name}</h1>
                    <p className="font-body text-xs text-zinc-400 mt-1">{new Date(report.created_at).toLocaleString()}</p>
                  </div>
                  <button onClick={() => downloadReport(report)} className="flex items-center gap-1.5 border border-zinc-200 px-3 py-1.5 text-xs font-body font-medium text-zinc-600 hover:bg-zinc-50 transition-colors" data-testid="download-report-btn">
                    <Download className="w-3 h-3" /> Download
                  </button>
                </div>
              </div>

              {/* Executive Summary */}
              <CollapsibleSection title="Executive Summary">
                <div className="flex justify-end mb-2">
                  <CopyButton text={report.executive_summary} label="Summary" />
                </div>
                <p className="font-body text-sm text-zinc-700 leading-relaxed whitespace-pre-wrap" data-testid="executive-summary">{report.executive_summary}</p>
              </CollapsibleSection>

              {/* Key Obligations */}
              <CollapsibleSection title="Key Obligations">
                <ul className="space-y-2 mt-2" data-testid="key-obligations">
                  {(report.key_obligations || []).map((o, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="font-mono text-xs text-zinc-400 mt-0.5 shrink-0">{String(i + 1).padStart(2, '0')}</span>
                      <span className="font-body text-sm text-zinc-700">{o}</span>
                    </li>
                  ))}
                </ul>
              </CollapsibleSection>

              {/* Risk Flags */}
              <CollapsibleSection title={`Risk Flags (${(report.risk_flags || []).length})`}>
                <div className="space-y-3 mt-2" data-testid="risk-flags">
                  {(report.risk_flags || []).sort((a, b) => {
                    const order = { High: 0, Medium: 1, Low: 2 };
                    return (order[a.severity] ?? 3) - (order[b.severity] ?? 3);
                  }).map((f, i) => (
                    <div key={i} className="border border-zinc-100 p-4" data-testid={`risk-flag-${i}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <SeverityBadge severity={f.severity} />
                        <span className="font-heading font-bold text-sm text-zinc-900">{f.category}</span>
                      </div>
                      {f.clause_excerpt && (
                        <p className="font-mono text-xs text-zinc-500 bg-zinc-50 p-2 mb-2 border-l-2 border-zinc-200 italic">"{f.clause_excerpt}"</p>
                      )}
                      <p className="font-body text-sm text-zinc-700">{f.concern}</p>
                    </div>
                  ))}
                </div>
              </CollapsibleSection>

              {/* Suggested Revisions */}
              <CollapsibleSection title="Suggested Revisions">
                <div className="flex justify-end mb-2">
                  <CopyButton text={(report.suggested_revisions || []).join('\n')} label="Revisions" />
                </div>
                <ul className="space-y-2 mt-2" data-testid="suggested-revisions">
                  {(report.suggested_revisions || []).map((r, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="font-mono text-xs text-zinc-400 mt-0.5 shrink-0">{String(i + 1).padStart(2, '0')}</span>
                      <span className="font-body text-sm text-zinc-700">{r}</span>
                    </li>
                  ))}
                </ul>
              </CollapsibleSection>

              {/* Extracted Clauses */}
              <CollapsibleSection title="Extracted Clauses" defaultOpen={false}>
                <div className="grid md:grid-cols-2 gap-3 mt-2" data-testid="extracted-clauses">
                  {Object.entries(clauseLabels).map(([key, label]) => {
                    const val = clauses[key];
                    if (!val) return null;
                    return (
                      <div key={key} className="border border-zinc-100 p-3" data-testid={`clause-${key}`}>
                        <span className="font-body text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1">{label}</span>
                        <p className="font-body text-xs text-zinc-700 leading-relaxed">{val}</p>
                      </div>
                    );
                  })}
                </div>
              </CollapsibleSection>

              {/* Lawyer Review Points */}
              <CollapsibleSection title="What to Review with a Lawyer">
                <div className="bg-blue-50 border border-blue-200 p-4 mt-2" data-testid="lawyer-review">
                  <div className="flex items-start gap-2 mb-3">
                    <AlertTriangle className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                    <p className="font-body text-xs text-blue-800 font-medium">We recommend discussing these points with a qualified attorney before signing.</p>
                  </div>
                  <ul className="space-y-2">
                    {(report.lawyer_review_points || []).map((p, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="font-mono text-xs text-blue-400 mt-0.5 shrink-0">{i + 1}.</span>
                        <span className="font-body text-xs text-blue-800">{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </CollapsibleSection>
            </div>

            {/* Right sidebar - 4 cols */}
            <div className="lg:col-span-4 space-y-4">
              <div className="bg-white border border-zinc-200 p-6 lg:sticky lg:top-24" data-testid="risk-sidebar">
                <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 font-body mb-4">Risk Assessment</p>
                <RiskScoreDisplay score={report.risk_score} />

                <div className="mt-6 pt-6 border-t border-zinc-100">
                  <p className="font-body text-xs font-medium text-zinc-700 mb-3">Risk Distribution</p>
                  <div className="space-y-2">
                    {['High', 'Medium', 'Low'].map(s => {
                      const count = (report.risk_flags || []).filter(f => f.severity === s).length;
                      return (
                        <div key={s} className="flex items-center justify-between">
                          <SeverityBadge severity={s} />
                          <span className="font-mono text-xs text-zinc-600">{count} flag{count !== 1 ? 's' : ''}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-zinc-100">
                  <p className="font-body text-xs font-medium text-zinc-700 mb-2">Clauses Found</p>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(clauseLabels).map(([key, label]) => {
                      const found = !!clauses[key];
                      return (
                        <span key={key} className={`px-2 py-0.5 text-xs font-body ${found ? 'bg-zinc-100 text-zinc-700' : 'bg-zinc-50 text-zinc-300 line-through'}`}>
                          {label}
                        </span>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-6 space-y-2">
                  <button onClick={() => downloadReport(report)} className="w-full border border-zinc-200 py-2 flex items-center justify-center gap-2 font-body text-sm text-zinc-600 hover:bg-zinc-50 transition-colors" data-testid="sidebar-download-btn">
                    <Download className="w-4 h-4" /> Download TXT
                  </button>
                  {isProPlus ? (
                    <button onClick={async () => {
                      try {
                        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports/${reportId}/pdf`, { credentials: 'include' });
                        if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Failed'); }
                        const blob = await res.blob();
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url; a.download = `risk-report-${report.file_name || 'contract'}.pdf`; a.click();
                        URL.revokeObjectURL(url);
                        toast.success('PDF downloaded!');
                      } catch (err) { toast.error(err.message || 'Failed to download PDF'); }
                    }} className="w-full bg-zinc-900 text-white hover:bg-zinc-800 py-2 flex items-center justify-center gap-2 font-body text-sm font-medium transition-colors" data-testid="download-pdf-btn">
                      <FileDown className="w-4 h-4" /> Download PDF
                    </button>
                  ) : (
                    <button onClick={() => toast.error('PDF reports are a Pro Plus feature. Upgrade in Settings.')} className="w-full border border-zinc-200 py-2 flex items-center justify-center gap-2 font-body text-sm text-zinc-400 cursor-not-allowed" data-testid="download-pdf-locked-btn">
                      <Lock className="w-3 h-3" /> Download PDF <span className="text-xs bg-zinc-100 px-1.5 py-0.5 font-bold">PRO+</span>
                    </button>
                  )}
                  {isProPlus ? (
                    <button onClick={handleGenerateEmail} disabled={emailLoading} className="w-full bg-brand text-white hover:bg-brand-hover py-2 flex items-center justify-center gap-2 font-body text-sm font-medium transition-colors disabled:opacity-50" data-testid="generate-email-btn">
                      {emailLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</> : <><Mail className="w-4 h-4" /> Negotiation Email</>}
                    </button>
                  ) : (
                    <button onClick={() => toast.error('Negotiation emails are a Pro Plus feature. Upgrade in Settings.')} className="w-full border border-zinc-200 py-2 flex items-center justify-center gap-2 font-body text-sm text-zinc-400 cursor-not-allowed" data-testid="generate-email-locked-btn">
                      <Lock className="w-3 h-3" /> Negotiation Email <span className="text-xs bg-zinc-100 px-1.5 py-0.5 font-bold">PRO+</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Email Modal */}
          {showEmailModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-6" style={{backgroundColor: 'rgba(0,0,0,0.5)'}} onClick={() => setShowEmailModal(false)}>
              <div className="bg-white border border-zinc-200 max-w-2xl w-full overflow-y-auto" style={{maxHeight: '80vh'}} onClick={(e) => e.stopPropagation()} data-testid="email-modal">
                <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-brand" />
                    <h3 className="font-heading font-bold text-base text-zinc-900">Negotiation Email</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => { navigator.clipboard.writeText(emailText); toast.success('Email copied to clipboard'); }} className="border border-zinc-200 px-3 py-1 text-xs font-body font-medium text-zinc-600 hover:bg-zinc-50 flex items-center gap-1" data-testid="copy-email-btn">
                      <Copy className="w-3 h-3" /> Copy
                    </button>
                    <button onClick={() => setShowEmailModal(false)} className="text-zinc-400 hover:text-zinc-600 p-1 text-lg leading-none font-bold">
                      X
                    </button>
                  </div>
                </div>
                <div className="p-6">
                  <pre className="font-body text-sm text-zinc-700 whitespace-pre-wrap leading-relaxed" data-testid="email-content">{emailText}</pre>
                </div>
                <div className="px-6 py-3 border-t border-zinc-100 bg-amber-50">
                  <p className="font-body text-xs text-amber-800">This is a suggested draft. Review and customize before sending. Not legal advice.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
